import React, { useState, useEffect } from 'react';
import type { User, Demand } from './types';
import { demandsAPI, usersAPI, contactsAPI, notificationsAPI, authAPI } from './api';
import { Login } from './components/Login';
import { Dashboard } from './components/Dashboard';
import { Inbox } from './components/Inbox';
import { DemandList } from './components/DemandList';
import { DemandDetail } from './components/DemandDetail';
import { AuditLogs } from './components/AuditLogs';
import { Profile } from './components/Profile';
import { useAlert } from './contexts/AlertContext';
import { AIRules } from './components/AIRules';
import { TemplateManagement } from './components/TemplateManagement';
import { ConsultaProtocolo } from './components/ConsultaProtocolo';
import { WhatsAppIntegration } from './components/WhatsAppIntegration';
import { GovFlowLogo } from './components/GovFlowLogo';
import { MobileHeader } from './components/mobile/MobileHeader';
import { BottomNavigation } from './components/mobile/BottomNavigation';
import { FloatingActionButton } from './components/mobile/FloatingActionButton';
import { MobileSearchDrawer } from './components/mobile/MobileSearchDrawer';
import { MobileNotificationsDrawer } from './components/mobile/MobileNotificationsDrawer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { MobileContactCard } from './components/mobile/MobileContactCard';
import { ContactDetailModal } from './components/mobile/ContactDetailModal';
import { CRMManagement } from './components/CRMManagement';
import { 
  LayoutDashboard, 
  Inbox as InboxIcon, 
  ListCollapse, 
  LogOut, 
  Sun, 
  Moon, 
  Sparkles, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Upload, 
  X, 
  Loader2, 
  Users, 
  Database, 
  UserCheck, 
  Bell, 
  Search, 
  User as UserIcon,
  CheckCircle,
  AlertTriangle,
  Mail,
  MessageSquare,
  Smartphone,
  FileText,
  Building2
} from 'lucide-react';

export const App: React.FC = () => {
  const { showAlert, showConfirm } = useAlert();
  const [token, setToken] = useState<string | null>(localStorage.getItem('gab_token'));
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Navegação
  const [currentTab, setCurrentTab] = useState<string>('dashboard');
  const [selectedDemandId, setSelectedDemandId] = useState<string | null>(null);
  const [selectedDemandForEdit, setSelectedDemandForEdit] = useState<boolean>(false);
  const [demands, setDemands] = useState<Demand[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(true);

  useEffect(() => {
    // Add dark class automatically based on OS preference (omitted since we default to dark)
    document.documentElement.classList.add('dark');
  }, []);

  // Busca Global
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');

  // Responsividade Barra Lateral
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Central de Notificações
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);

  // Mobile state
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const fetchNotifications = async () => {
    try {
      const data = await notificationsAPI.list();
      setNotifications(data);
    } catch (e) {
      console.error('Falha ao buscar notificações', e);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
      // Atualizar a cada minuto
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Modal Nova Demanda
  const [isNewDemandOpen, setIsNewDemandOpen] = useState(false);
  const [newDemandSource, setNewDemandSource] = useState<'WHATSAPP' | 'EMAIL' | 'PDF' | 'IMAGE' | 'MANUAL'>('MANUAL');
  const [newDemandTitle, setNewDemandTitle] = useState('');
  const [newDemandSolicitante, setNewDemandSolicitante] = useState('');
  const [newDemandCity, setNewDemandCity] = useState('');
  const [newDemandArea, setNewDemandArea] = useState('Saúde');
  const [newDemandCabinet, setNewDemandCabinet] = useState<'STATE' | 'FEDERAL' | 'BOTH'>('STATE');
  const [newDemandValue, setNewDemandValue] = useState('');
  const [newDemandSummary, setNewDemandSummary] = useState('');
  const [newDemandResponsible, setNewDemandResponsible] = useState('');
  const [newDemandExtraction, setNewDemandExtraction] = useState<any>(null);
  const [newDemandFileName, setNewDemandFileName] = useState('');
  const [newDemandFileUrl, setNewDemandFileUrl] = useState('');
  const [newDemandFileSize, setNewDemandFileSize] = useState(0);
  const [newDemandFileType, setNewDemandFileType] = useState('');
  
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isProcessingIA, setIsProcessingIA] = useState(false);

  // Estados para Gestão de Usuários
  const [usersList, setUsersList] = useState<User[]>([]);
  const [rolesList, setRolesList] = useState<any[]>([]);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRoleId, setNewUserRoleId] = useState('');
  const [newUserPhone, setNewUserPhone] = useState('');
  const [newUserJobTitle, setNewUserJobTitle] = useState('');
  const [newUserThematicAreas, setNewUserThematicAreas] = useState<string[]>([]);
  const [newUserPrefs, setNewUserPrefs] = useState<{ channel: string; receiveScope: string }[]>([
    { channel: 'INTERNAL', receiveScope: 'ALL' },
    { channel: 'WHATSAPP', receiveScope: 'NONE' },
    { channel: 'EMAIL', receiveScope: 'NONE' },
  ]);

  // Removido console.log mock

  // Estados para Gestão de Contatos do Gabinete
  const [isEditContactOpen, setIsEditContactOpen] = useState(false);
  const [selectedContactForEdit, setSelectedContactForEdit] = useState<any | null>(null);
  const [editContactName, setEditContactName] = useState('');
  const [editContactType, setEditContactType] = useState<any>('OUTRO');
  const [editContactResponsible, setEditContactResponsible] = useState('');
  const [editContactCity, setEditContactCity] = useState('');
  const [editContactUf, setEditContactUf] = useState('SP');
  const [editContactPhone, setEditContactPhone] = useState('');
  const [editContactEmail, setEditContactEmail] = useState('');
  const [editContactAddress, setEditContactAddress] = useState('');
  const [editContactWebsite, setEditContactWebsite] = useState('');
  
  // Estados para Detalhes do Contato
  const [selectedContactForDetail, setSelectedContactForDetail] = useState<any | null>(null);
  const [isDetailContactOpen, setIsDetailContactOpen] = useState(false);

  // Rota Pública
  const isPublicQueryRoute = window.location.pathname === '/consulta-protocolo';

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    if (!isDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  };

  const loadDemandsData = async () => {
    try {
      const res = await demandsAPI.list();
      setDemands(res.data);
      const contactsRes = await contactsAPI.list();
      setContacts(contactsRes);
    } catch (e) {
      console.warn('Erro ao atualizar dados das demandas.');
    }
  };

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark');
    }

    if (isPublicQueryRoute) {
      setLoading(false);
      return;
    }

    const verifyAuth = async () => {
      if (token) {
        try {
          // Busca os dados reais do usuário autenticado no backend
          const realUser = await authAPI.me();
          setCurrentUser(realUser);
          
          // Busca demandas e contatos iniciais
          await loadDemandsData();
        } catch (e) {
          console.error("Falha ao verificar autenticação", e);
          localStorage.removeItem('gab_token');
          setToken(null);
          setCurrentUser(null);
        }
      }
      setLoading(false);
    };

    verifyAuth();
  }, [token]);

  const handleLoginSuccess = (userToken: string, user: User) => {
    setToken(userToken);
    setCurrentUser(user);
    loadDemandsData();
  };

  const handleLogout = () => {
    localStorage.removeItem('gab_token');
    setToken(null);
    setCurrentUser(null);
  };

  // Simular processamento por IA de Cadastro Manual
  const handleProcessManualIA = async () => {
    if (!newDemandTitle && !newDemandSummary) {
      showAlert('error', 'Erro', 'Por favor, preencha o assunto ou a descrição antes de acionar a IA.');
      return;
    }
    setIsProcessingIA(true);
    try {
      const suggestions = await demandsAPI.processIA(newDemandTitle, newDemandSummary);
      setNewDemandCity(suggestions.city);
      setNewDemandArea(suggestions.themeArea);
      setNewDemandCabinet(suggestions.cabinet as any);
      setNewDemandSolicitante(suggestions.solicitanteName);
      setNewDemandValue(String(suggestions.valueRequested));
      setNewDemandSummary(suggestions.summary);
      showAlert('info', 'Aviso', 'A IA analisou os dados inseridos e sugeriu o preenchimento automático dos campos!');
    } catch (e: any) {
      showAlert('error', 'Erro', 'Erro ao processar dados com IA: ' + e.message);
    } finally {
      setIsProcessingIA(false);
    }
  };

  // Upload de arquivos e leitura via OCR/IA real no backend
  const handleSimulatedUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    setUploadLoading(true);
    setUploadSuccess(false);

    try {
      console.log('Enviando arquivo para processamento no backend...', file.name);
      const res = await demandsAPI.processDocument(file);
      if (res.success && res.extraction) {
        const ext = res.extraction;
        setNewDemandTitle(ext.assunto_titulo_curto || '');
        setNewDemandSolicitante(ext.solicitante || '');
        setNewDemandCity(ext.cidade || '');
        setNewDemandArea(ext.area_tematica || 'Saúde');
        
        let cab: 'STATE' | 'FEDERAL' | 'BOTH' = 'STATE';
        if (ext.gabinete_destinatario?.includes('Federal') || ext.gabinete_destinatario?.includes('Augusto')) {
          cab = 'FEDERAL';
        } else if (ext.gabinete_destinatario?.includes('Ambos')) {
          cab = 'BOTH';
        }
        setNewDemandCabinet(cab);
        let val = ext.valor_solicitado || '';
        if (typeof val === 'string') {
          val = val.replace(/[^\d.,]/g, '').replace(/\./g, '').replace(',', '.');
        }
        setNewDemandValue(val);
        setNewDemandSummary(ext.resumo_didatico || '');
        setNewDemandResponsible(ext.responsavel || '');
        setNewDemandExtraction(ext);
        
        setNewDemandFileName(res.fileName || file.name);
        setNewDemandFileUrl(res.fileUrl || '');
        setNewDemandFileSize(res.fileSize || file.size);
        setNewDemandFileType(res.fileType || file.type);
        
        setUploadSuccess(true);
      } else {
        showAlert('error', 'Erro', 'Erro ao extrair dados do documento.');
      }
    } catch (err: any) {
      console.error('Erro no upload real:', err);
      showAlert('error', 'Erro', `Erro ao processar o documento: ${err.response?.data?.error || err.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleCreateManualDemand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDemandTitle || !newDemandSolicitante || !newDemandCity) return;

    try {
      await demandsAPI.create({
        title: newDemandTitle,
        solicitanteName: newDemandSolicitante,
        city: newDemandCity,
        themeArea: newDemandArea,
        valueRequested: newDemandValue,
        responsibleName: newDemandResponsible,
        summary: newDemandSummary,
        cabinet: newDemandCabinet,
        source: 'MANUAL',
        fileName: newDemandFileName,
        fileUrl: newDemandFileUrl,
        fileSize: newDemandFileSize,
        fileType: newDemandFileType,
        extraction: newDemandExtraction
      });

      // Se for real/API online, criaria no banco. Como estamos em mock unificado, a API simula.
      setNewDemandTitle('');
      setNewDemandSolicitante('');
      setNewDemandCity('');
      setNewDemandArea('Saúde');
      setNewDemandValue('');
      setNewDemandSummary('');
      setNewDemandCabinet('STATE');
      setNewDemandResponsible('');
      setNewDemandFileName('');
      setNewDemandFileUrl('');
      setNewDemandFileSize(0);
      setNewDemandFileType('');
      setUploadSuccess(false);
      setIsNewDemandOpen(false);

      loadDemandsData();
      setCurrentTab('inbox');
      showAlert('success', 'Sucesso', 'Demanda encaminhada com sucesso! Triagem de IA concluída.');
    } catch (e) {
      showAlert('error', 'Erro', 'Erro ao cadastrar demanda.');
    }
  };

  // --- GESTÃO DE USUÁRIOS E PERMISSÕES ---
  const loadUsersData = async () => {
    try {
      const uRes = await usersAPI.list();
      setUsersList(uRes);
      const rRes = await usersAPI.listRoles();
      setRolesList(rRes);
    } catch (e) {
      console.warn('Erro ao carregar usuários ou cargos da API.');
    }
  };

    const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName || !newUserEmail || !newUserPassword || !newUserRoleId) {
      showAlert('error', 'Erro', 'Preencha todos os campos obrigatórios.');
      return;
    }
    try {
      await usersAPI.create({
        name: newUserName,
        email: newUserEmail,
        password: newUserPassword,
        roleId: newUserRoleId,
        phone: newUserPhone,
        jobTitle: newUserJobTitle,
        thematicAreas: newUserThematicAreas,
        notificationPrefs: newUserPrefs,
      });
      showAlert('success', 'Sucesso', 'Usuário criado com sucesso!');
      setIsAddUserOpen(false);
      // Reset form
      setNewUserName('');
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserRoleId('');
      setNewUserPhone('');
      setNewUserJobTitle('');
      setNewUserThematicAreas([]);
      loadUsersData();
    } catch (err: any) {
      showAlert('error', 'Erro', 'Erro ao criar usuário: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleOpenEditUser = (user: any) => {
    setSelectedUserForEdit(user);
    setNewUserName(user.name);
    setNewUserEmail(user.email);
    setNewUserRoleId(user.roleId || user.roleDetails?.id || '');
    setNewUserPhone(user.phone || '');
    setNewUserJobTitle(user.jobTitle || '');
    setNewUserThematicAreas(user.thematicAreas?.map((a: any) => a.name) || []);
    setNewUserPrefs(user.notificationPrefs?.length > 0 ? user.notificationPrefs : [
      { channel: 'INTERNAL', receiveScope: 'ALL' },
      { channel: 'WHATSAPP', receiveScope: 'NONE' },
      { channel: 'EMAIL', receiveScope: 'NONE' },
    ]);
    setIsEditUserOpen(true);
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserForEdit) return;
    try {
      await usersAPI.update(selectedUserForEdit.id, {
        name: newUserName,
        email: newUserEmail,
        roleId: newUserRoleId,
        isActive: true,
        phone: newUserPhone,
        jobTitle: newUserJobTitle,
        thematicAreas: newUserThematicAreas,
        notificationPrefs: newUserPrefs,
      });
      showAlert('success', 'Sucesso', 'Usuário atualizado com sucesso!');
      setIsEditUserOpen(false);
      setSelectedUserForEdit(null);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserRoleId('');
      setNewUserPhone('');
      setNewUserJobTitle('');
      setNewUserThematicAreas([]);
      loadUsersData();
    } catch (err: any) {
      showAlert('error', 'Erro', 'Erro ao editar usuário: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRemoveUser = async (id: string, name: string) => {
    showConfirm('Excluir Usuário', `Tem certeza que deseja remover o usuário "${name}"?`, async () => {
      try {
        await usersAPI.delete(id);
        showAlert('success', 'Sucesso', 'Usuário removido com sucesso!');
        loadUsersData();
      } catch (err: any) {
        showAlert('error', 'Erro', 'Erro ao remover usuário: ' + (err.response?.data?.error || err.message));
      }
    }, true);
  };

  // --- GESTÃO DE CONTATOS DO GABINETE ---
  const handleOpenNewContact = () => {
    setSelectedContactForEdit('NEW');
    setEditContactName('');
    setEditContactType('OUTRO');
    setEditContactResponsible('');
    setEditContactCity('');
    setEditContactUf('SP');
    setEditContactPhone('');
    setEditContactEmail('');
    setEditContactAddress('');
    setEditContactWebsite('');
    setIsEditContactOpen(true);
  };

  const handleOpenEditContact = (contact: any) => {
    setSelectedContactForEdit(contact);
    setEditContactName(contact.nameEntity);
    setEditContactType(contact.type);
    setEditContactResponsible(contact.responsible || '');
    setEditContactCity(contact.city);
    setEditContactUf(contact.uf);
    setEditContactPhone(contact.phone || '');
    setEditContactEmail(contact.email || '');
    setEditContactAddress(contact.address || '');
    setEditContactWebsite(contact.website || '');
    setIsEditContactOpen(true);
  };

  const handleDeleteContact = async (contactId: string, contactName: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o contato "${contactName}"?`)) return;
    try {
      await contactsAPI.delete(contactId);
      showAlert('success', 'Sucesso', 'Contato excluído com sucesso!');
      loadDemandsData(); // Reload contacts
    } catch (error: any) {
      showAlert('error', 'Erro', error.response?.data?.error || 'Erro ao excluir contato.');
    }
  };

  const handleUpdateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContactForEdit) return;

    try {
      const data = {
        nameEntity: editContactName,
        type: editContactType,
        responsible: editContactResponsible,
        city: editContactCity,
        uf: editContactUf,
        phone: editContactPhone,
        email: editContactEmail,
        address: editContactAddress,
        website: editContactWebsite
      };
      let res;
      if (selectedContactForEdit === 'NEW') {
        res = await contactsAPI.create(data);
      } else {
        res = await contactsAPI.update(selectedContactForEdit.id, data);
      }

      if (res.success) {
        showAlert('success', 'Sucesso', selectedContactForEdit === 'NEW' ? 'Contato criado com sucesso!' : 'Contato atualizado com sucesso!');
        setIsEditContactOpen(false);
        setSelectedContactForEdit(null);
        loadDemandsData();
      }
    } catch (err: any) {
      showAlert('error', 'Erro', 'Erro ao salvar contato: ' + err.message);
    }
  };

  const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    
    // Validar tipo de arquivo
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (ext !== '.csv') {
      showAlert('error', 'Arquivo Inválido', 'Por favor, selecione um arquivo no formato CSV (.csv)');
      return;
    }

    try {
      showAlert('info', 'Processando', 'Importando contatos em lote, por favor aguarde...');
      const res = await contactsAPI.importCSV(file);
      if (res.success) {
        showAlert('success', 'Importação Concluída', `Sucesso: ${res.imported} contatos importados. Pulados (duplicados): ${res.skipped}.`);
        loadDemandsData(); // Recarregar contatos do painel
      }
    } catch (err: any) {
      showAlert('error', 'Erro na Importação', err.response?.data?.error || err.message);
    } finally {
      // Limpar o valor do input para permitir re-upload do mesmo arquivo se necessário
      e.target.value = '';
    }
  };

  useEffect(() => {
    if (currentTab === 'users' && token) {
      loadUsersData();
    }
    if (currentTab === 'settings' && token) {
      // Placeholder para carregar settings no futuro
    }
  }, [currentTab, token]);

  // Pesquisa Global Handlers
  const handleGlobalSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setGlobalSearchQuery(val);
    if (val.trim() !== '') {
      setSelectedDemandId(null);
      setCurrentTab('demands'); // Redirecionar para visualização de lista com filtro de busca ativa
    }
  };

  // Mapear Abas por Permissão Dinâmica
  const getVisibleTabs = () => {
    if (!currentUser) return [];
    const role = currentUser.role;

    const allTabs = [
      { id: 'dashboard', label: 'Dashboard Executivo', icon: <LayoutDashboard className="w-4 h-4" /> },
      { id: 'inbox', label: 'Central de Triagem', icon: <InboxIcon className="w-4 h-4" />, badge: demands.filter(d => d.status === 'RECEBIDA').length },
      { id: 'demands', label: 'Gestão de Demandas', icon: <ListCollapse className="w-4 h-4" /> },
      { id: 'crm', label: 'CRM & Recursos', icon: <Building2 className="w-4 h-4" /> },
      { id: 'contacts', label: 'Contatos Gabinete', icon: <UserCheck className="w-4 h-4" /> },
      { id: 'users', label: 'Gestão de Usuários', icon: <Users className="w-4 h-4" /> },
      { id: 'templates', label: 'Templates Oficiais', icon: <FileText className="w-4 h-4" /> },
      { id: 'integrations', label: 'WhatsApp & Integrações', icon: <MessageSquare className="w-4 h-4" /> },
      { id: 'audit', label: 'Auditoria & Logs', icon: <Database className="w-4 h-4" /> },
      { id: 'profile', label: 'Meu Perfil', icon: <UserIcon className="w-4 h-4" /> }
    ];

    if (role === 'ADMIN' || role === 'ROLE_ADMIN') {
      return allTabs;
    }
    if (role === 'ROLE_CHEFE_GABINETE') {
      return allTabs;
    }
    if (role === 'SHARED_ASSESSOR' || role === 'ROLE_ASSESSOR') {
      // Assessor vê Dashboard, Caixa, Demandas, CRM, Contatos + Perfil
      return allTabs.filter(tab => ['dashboard', 'inbox', 'demands', 'crm', 'contacts', 'profile'].includes(tab.id));
    }
    if (role === 'STATE_CABINET' || role === 'FEDERAL_CABINET' || role === 'ROLE_PARLAMENTAR') {
      // Parlamentar visualiza Dashboard, Caixa de entrada, Gestão de Demandas, CRM + Perfil
      return allTabs.filter(tab => ['dashboard', 'inbox', 'demands', 'crm', 'profile'].includes(tab.id));
    }
    if (role === 'ROLE_CONSULTA') {
      // Consulta visualiza apenas Dashboard, Caixa e Demandas + Perfil
      return allTabs.filter(tab => ['dashboard', 'inbox', 'demands', 'profile'].includes(tab.id));
    }

    return allTabs.filter(tab => ['dashboard', 'inbox', 'demands', 'profile'].includes(tab.id));
  };

  const tabsToRender = getVisibleTabs();

  // Marcar notificações como lidas
  const markAllNotificationsRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Erro ao marcar todas como lidas:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-500 font-sans">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-2" />
        Carregando painel do gabinete parlamentar...
      </div>
    );
  }

  if (isPublicQueryRoute) {
    return <ConsultaProtocolo />;
  }

  if (!token || !currentUser) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100 transition-colors duration-300 font-sans">

      {/* ─── MOBILE: Header + Bottom Nav + FAB + Search ─── */}
      <MobileHeader
        title={selectedDemandId ? undefined : undefined}
        showBack={!!selectedDemandId}
        onBack={() => { setSelectedDemandId(null); setSelectedDemandForEdit(false); loadDemandsData(); }}
        onSearchOpen={() => setIsMobileSearchOpen(true)}
        onNotificationsOpen={() => setIsNotificationsOpen(true)}
        onNewDemand={() => setIsNewDemandOpen(true)}
        unreadCount={unreadCount}
        canCreate={currentUser.permissions?.includes('demand:edit')}
      />

      <MobileSearchDrawer
        isOpen={isMobileSearchOpen}
        onClose={() => setIsMobileSearchOpen(false)}
        query={globalSearchQuery}
        onQueryChange={(q) => { setGlobalSearchQuery(q); if (q) { setSelectedDemandId(null); setCurrentTab('demands'); } }}
      />

      <MobileNotificationsDrawer
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        notifications={notifications}
        onMarkAllRead={markAllNotificationsRead}
        onOpenDemand={(demandId) => {
          setSelectedDemandId(demandId);
          setCurrentTab('demands');
        }}
      />

      <ErrorBoundary silent>
        <FloatingActionButton
          onNewDemand={() => setIsNewDemandOpen(true)}
          onNewContact={handleOpenNewContact}
          onUploadDocument={() => {
            setNewDemandSource('PDF');
            setIsNewDemandOpen(true);
          }}
          canCreate={!selectedDemandId && currentUser.permissions?.includes('demand:edit')}
        />
      </ErrorBoundary>

      {/* 1. SIDEBAR LATERAL RESPONSIVA - Oculta em mobile */}
      <aside className={`hidden md:flex bg-white border-r border-slate-200 dark:bg-slate-900/40 dark:border-slate-800/80 flex-col justify-between p-5 shrink-0 z-20 text-left transition-all duration-300 relative ${
        isSidebarCollapsed ? 'w-[80px] items-center' : 'w-[270px]'
      }`}>
        
        {/* Collapse Toggle */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute right-[-12px] top-8 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-200 dark:hover:bg-slate-800 p-1 rounded-full text-slate-500 z-30 transition-all shadow-sm"
          title={isSidebarCollapsed ? "Expandir Menu" : "Recolher Menu"}
        >
          {isSidebarCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>

        <div className="space-y-6 w-full overflow-y-auto max-h-[85vh] no-scrollbar">
          {/* Brand GovFlow */}
          <div className={`flex items-center gap-2 pb-4 border-b border-slate-100 dark:border-slate-800/60 ${isSidebarCollapsed ? 'justify-center px-0' : 'px-1'}`}>
            {isSidebarCollapsed ? (
              <GovFlowLogo variant="icon" size="xs" />
            ) : (
              <GovFlowLogo variant="full" size="sm" />
            )}
          </div>

          {/* User Info */}
          {!isSidebarCollapsed ? (
            <div className="px-2 py-3 bg-slate-100/50 dark:bg-slate-900/60 border border-slate-200/30 dark:border-slate-800/40 rounded-2xl flex flex-col gap-1 w-full">
              <span className="font-bold text-xs truncate">{currentUser.name}</span>
              <span className="text-[9px] text-slate-400 font-mono truncate">{currentUser.email}</span>
              <span className="inline-flex max-w-fit px-2 py-0.5 mt-1 font-bold rounded text-[9px] uppercase text-white"
                style={{ background: 'linear-gradient(135deg, #1B2D72 0%, #1565C0 100%)' }}
              >
                {currentUser.role.replace('ROLE_', '').replace('_', ' ')}
              </span>
            </div>
          ) : (
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 text-white"
              style={{ background: 'linear-gradient(135deg, #1B2D72 0%, #1565C0 100%)' }}
              title={currentUser.name}
            >
              {currentUser.name.charAt(0)}
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="space-y-1 w-full">
            {tabsToRender.map(tab => (
              <button
                key={tab.id}
                onClick={() => { 
                  setCurrentTab(tab.id); 
                  setSelectedDemandId(null);
                  setSelectedDemandForEdit(false);
                }}
                className={`w-full flex items-center justify-between p-2.5 rounded-xl transition-all ${
                  currentTab === tab.id && !selectedDemandId
                    ? 'nav-active'
                    : 'hover:bg-slate-500/5 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                title={isSidebarCollapsed ? tab.label : undefined}
              >
                <div className="flex items-center gap-3">
                  {tab.icon}
                  {!isSidebarCollapsed && <span className="text-xs font-semibold">{tab.label}</span>}
                </div>
                {!isSidebarCollapsed && tab.badge !== undefined && tab.badge > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                    currentTab === tab.id ? 'bg-white text-blue-600' : 'bg-red-500 text-white animate-pulse'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 p-3 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-red-550/10 hover:text-red-500 hover:border-red-500/20 text-slate-500 rounded-xl transition-all text-xs font-semibold ${
            isSidebarCollapsed ? 'w-10 h-10 justify-center p-0' : 'w-full'
          }`}
          title={isSidebarCollapsed ? "Sair do Sistema" : undefined}
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {!isSidebarCollapsed && <span>Sair do Sistema</span>}
        </button>
      </aside>

      {/* 2. CONTEÚDO PRINCIPAL COM BARRA DE PESQUISA GLOBAL */}
      <main className="flex-1 flex flex-col min-w-0 min-h-screen relative overflow-y-auto pb-20 md:pb-0">
        
        {/* Cabeçalho Superior Global — Desktop apenas */}
        <header className="hidden md:flex bg-white border-b border-slate-200 dark:bg-slate-950/20 dark:border-slate-900 px-6 py-4 flex-col sm:flex-row justify-between items-center gap-4 z-10 shrink-0">
          
          {/* Pesquisa Global */}
          <div className="relative w-full max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
              <Search className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Pesquisar demandas, cidades, entidades, protocolos ou contatos..."
              className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-900 border border-slate-250 dark:border-slate-800 rounded-xl text-xs focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder:text-slate-400"
              value={globalSearchQuery}
              onChange={handleGlobalSearchChange}
            />
            {globalSearchQuery && (
              <button 
                onClick={() => setGlobalSearchQuery('')} 
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-650"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-4 shrink-0">
            {/* Botão Nova Demanda */}
            {currentUser.permissions?.includes('demand:edit') && (
              <button
                onClick={() => setIsNewDemandOpen(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-blue-500/10 transition-all focus:outline-none"
              >
                <Plus className="w-4 h-4" />
                Nova Demanda
              </button>
            )}

            {/* Central de Notificações */}
            <div className="relative">
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 rounded-xl transition-all relative"
                title="Notificações"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-red-500 border-2 border-white dark:border-slate-900 rounded-full animate-bounce"></span>
                )}
              </button>

              {/* Popover Notificações */}
              {isNotificationsOpen && (
                <div className="absolute right-0 mt-2.5 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl p-4 text-xs z-50 animate-in fade-in slide-in-from-top-2 duration-200 text-left">
                  <div className="flex justify-between items-center pb-2 border-b border-slate-150 dark:border-slate-800 mb-2">
                    <span className="font-bold">Notificações Recentes</span>
                    <button 
                      onClick={markAllNotificationsRead} 
                      className="text-[10px] text-blue-500 hover:underline"
                    >
                      Ler todas
                    </button>
                  </div>

                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <p className="text-center text-slate-400 py-4">Nenhuma notificação ainda.</p>
                    ) : (
                      notifications.map((n: any) => (
                        <div 
                          key={n.id} 
                          onClick={async () => {
                            if (!n.read) {
                              try {
                                await notificationsAPI.markAsRead(n.id);
                                setNotifications(notifications.map(notif => notif.id === n.id ? { ...notif, read: true } : notif));
                              } catch (e) { console.error(e); }
                            }
                            if (n.demandId) {
                              setSelectedDemandId(n.demandId);
                              setCurrentTab('demands');
                              setIsNotificationsOpen(false);
                            }
                          }}
                          className={`p-2.5 rounded-xl border transition-all flex gap-2 cursor-pointer hover:shadow-md ${
                            n.read ? 'bg-slate-50/50 dark:bg-slate-900/30 border-slate-100 dark:border-slate-800/40 opacity-70' : 'bg-blue-50/20 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900 hover:bg-blue-50/40 dark:hover:bg-blue-900/30'
                          }`}
                        >
                          <div className="mt-0.5">
                            {(n.type === 'LOW_CONFIDENCE' || n.type === 'WARNING') 
                              ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> 
                              : n.type === 'NEW_DEMAND' 
                              ? <Bell className="w-3.5 h-3.5 text-blue-500" />
                              : <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />}
                          </div>
                          <div className="flex-1 space-y-0.5">
                            <div className="font-bold text-slate-850 dark:text-slate-205 flex justify-between">
                              <span>{n.title}</span>
                              <span className="text-[9px] text-slate-400 font-mono font-normal">
                                {new Date(n.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 leading-normal">{n.content}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <button 
                    onClick={() => setIsNotificationsOpen(false)}
                    className="w-full text-center py-2 border-t border-slate-100 dark:border-slate-800 mt-2 text-slate-450 hover:text-slate-650 hover:underline transition-all block font-semibold text-[10px]"
                  >
                    Fechar Notificações
                  </button>
                </div>
              )}
            </div>

            {/* Alternador Dark/Light */}
            <button
              onClick={toggleDarkMode}
              className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-900 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 rounded-xl transition-all"
              title="Alternar Tema"
            >
              {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
        </header>

        {/* Corpo de Componentes Dinâmicos */}
        <div className="flex-1 p-6 relative">
          <ErrorBoundary>
            {selectedDemandId ? (
              <DemandDetail
                demandId={selectedDemandId}
                initialEditMode={selectedDemandForEdit}
                userRole={currentUser.role}
                userPermissions={currentUser.permissions || []}
                onBack={() => { 
                  setSelectedDemandId(null); 
                  setSelectedDemandForEdit(false);
                  loadDemandsData(); 
                }}
              />
            ) : (
            <>
              {currentTab === 'dashboard' && (
                <Dashboard 
                  demands={demands} 
                  onOpenNewDemand={() => setIsNewDemandOpen(true)} 
                />
              )}
              {currentTab === 'inbox' && (
                <Inbox
                  demands={demands}
                  globalSearch={globalSearchQuery}
                  onRefresh={loadDemandsData}
                  onSelectDemand={(id) => { setSelectedDemandId(id); setSelectedDemandForEdit(false); }}
                  onSelectDemandEdit={(id) => { setSelectedDemandId(id); setSelectedDemandForEdit(true); }}
                  userRole={currentUser.role}
                  userPermissions={currentUser.permissions || []}
                />
              )}
              {currentTab === 'demands' && (
                <DemandList
                  demands={demands}
                  globalSearch={globalSearchQuery}
                  onSelectDemand={(id) => { setSelectedDemandId(id); setSelectedDemandForEdit(false); }}
                  onSelectDemandEdit={(id) => { setSelectedDemandId(id); setSelectedDemandForEdit(true); }}
                  userRole={currentUser.role}
                  userPermissions={currentUser.permissions || []}
                />
              )}
              {currentTab === 'crm' && (
                <CRMManagement />
              )}

              {/* ABA: CONTATOS GABINETE */}
              {currentTab === 'contacts' && (
                <div className="space-y-6 text-left animate-in fade-in duration-200">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold tracking-tight font-sans">Contatos do Gabinete</h1>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Banco unificado de solicitantes, prefeituras, hospitais e entidades regionais.</p>
                    </div>
                    {currentUser.permissions?.includes('demand:edit') && (
                      <div className="hidden md:flex items-center gap-3 shrink-0">
                        {/* Input de CSV oculto */}
                        <input
                          type="file"
                          id="import-csv-file"
                          accept=".csv"
                          className="hidden"
                          onChange={handleImportCSV}
                        />
                        <label
                          htmlFor="import-csv-file"
                          className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-slate-700 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-2xl shadow-sm transition-all hover:scale-105 cursor-pointer"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                          Importar Planilha (.csv)
                        </label>
                        <button
                          onClick={handleOpenNewContact}
                          className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-2xl shadow-lg shadow-blue-500/20 transition-all hover:scale-105"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                          Novo Contato
                        </button>
                      </div>
                    )}
                  </div>

                  {/* MOBILE: add button + cards */}
                  <div className="md:hidden space-y-3">
                    {currentUser.permissions?.includes('demand:edit') && (
                      <button
                        onClick={handleOpenNewContact}
                        className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-2xl shadow-lg shadow-blue-500/20 transition-all"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Novo Contato
                      </button>
                    )}
                    {contacts.map(contact => (
                      <ErrorBoundary key={contact!.id}>
                        <MobileContactCard
                          contact={contact}
                          onViewDemands={() => {
                            setGlobalSearchQuery(contact!.nameEntity);
                            setCurrentTab('demands');
                          }}
                          onDeleteContact={handleDeleteContact}
                          onCardClick={(c) => {
                            setSelectedContactForDetail(c);
                            setIsDetailContactOpen(true);
                          }}
                        />
                      </ErrorBoundary>
                    ))}
                    {contacts.length === 0 && (
                      <p className="text-center text-slate-400 py-8">Nenhum contato encontrado.</p>
                    )}
                  </div>

                  {/* DESKTOP: table */}
                  <div className="hidden md:block glass-panel p-5 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="p-3">Entidade/Solicitante</th>
                          <th className="p-3">Tipo</th>
                          <th className="p-3">Responsável</th>
                          <th className="p-3">Cidade/UF</th>
                          <th className="p-3">Telefone</th>
                          <th className="p-3">E-mail</th>
                          <th className="p-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="p-10 text-center text-slate-500 italic">
                              Nenhum contato registrado no sistema ainda.
                            </td>
                          </tr>
                        ) : (
                          contacts.map(contact => (
                            <tr key={contact!.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                              <td className="p-3 font-semibold text-slate-800 dark:text-slate-200">{contact!.nameEntity}</td>
                              <td className="p-3"><span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-900 rounded font-semibold text-blue-500">{contact!.type}</span></td>
                              <td className="p-3 text-slate-500">{contact!.responsible || '-'}</td>
                              <td className="p-3 text-slate-500 font-medium">{contact!.city} - {contact!.uf}</td>
                              <td className="p-3 text-slate-500 font-mono">{contact!.phone || '-'}</td>
                              <td className="p-3 text-slate-500">{contact!.email || '-'}</td>
                              <td className="p-3 space-x-3">
                                <button 
                                  onClick={() => {
                                    setSelectedContactForDetail(contact);
                                    setIsDetailContactOpen(true);
                                  }}
                                  className="text-xs text-blue-500 hover:underline font-semibold"
                                >
                                  Ver Informações
                                </button>
                                <button 
                                  onClick={() => {
                                    setGlobalSearchQuery(contact!.nameEntity);
                                    setCurrentTab('demands');
                                  }}
                                  className="text-xs text-slate-550 hover:underline font-semibold"
                                >
                                  Demandas
                                </button>
                                <button 
                                  onClick={() => handleOpenEditContact(contact)}
                                  className="text-xs text-amber-500 hover:underline font-semibold"
                                >
                                  Editar
                                </button>
                                <button 
                                  onClick={() => handleDeleteContact(contact!.id, contact!.nameEntity)}
                                  className="text-xs text-red-500 hover:underline font-semibold"
                                >
                                  Excluir
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>


                </div>
              )}


              {/* ABA: RELATÓRIOS & EMENDAS */}
              {currentTab === 'reports' && (
                <div className="space-y-6 text-left animate-in fade-in duration-200">
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight font-sans">Relatórios & Emendas Orçamentárias</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Exportação consolidada e acompanhamento financeiro de emendas e intervenções.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="glass-panel p-6 rounded-3xl space-y-3">
                      <h3 className="font-bold text-sm">Ofícios Recebidos e Triados</h3>
                      <p className="text-slate-500 text-xs">Compilado de todas as demandas extraídas com logs de auditoria e timelines consolidadas.</p>
                      <button className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs transition-all">
                        Exportar Relatório PDF
                      </button>
                    </div>

                    <div className="glass-panel p-6 rounded-3xl space-y-3">
                      <h3 className="font-bold text-sm">Liberação de Recursos de SP</h3>
                      <p className="text-slate-500 text-xs">Gráfico financeiro de emendas parlamentares aprovadas no Estado de São Paulo (Dani Alonso).</p>
                      <button className="w-full py-2 bg-slate-905 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-950 font-bold rounded-xl text-xs transition-all">
                        Exportar Planilha Excel
                      </button>
                    </div>

                    <div className="glass-panel p-6 rounded-3xl space-y-3">
                      <h3 className="font-bold text-sm">Distribuição Federal</h3>
                      <p className="text-slate-500 text-xs">Histórico orçamentário consolidado das emendas parlamentares federais (Capitão Augusto).</p>
                      <button className="w-full py-2 bg-slate-905 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-white text-white dark:text-slate-950 font-bold rounded-xl text-xs transition-all">
                        Imprimir Consolidação
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ABA: TREINAMENTO IA */}
              {currentTab === 'rules' && <AIRules />}

              {/* ABA: INTEGRAÇÕES — WhatsApp Business */}
              {currentTab === 'integrations' && <WhatsAppIntegration />}

              {/* ABA: SIMULADOR (removido — redirecionar para integrações) */}
              {currentTab === 'simulator' && (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="font-bold text-slate-600 dark:text-slate-400">Simulador removido</p>
                    <p className="text-sm text-slate-400 mt-1">Configure a integração real em Configurações → WhatsApp.</p>
                    <button
                      className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold"
                      onClick={() => setCurrentTab('integrations')}
                    >
                      Abrir Configurações de Integração
                    </button>
                  </div>
                </div>
              )}

              {/* ABA: TEMPLATES OFICIAIS */}
              {currentTab === 'templates' && <TemplateManagement />}

              {/* ABA: USUÁRIOS */}
              {currentTab === 'users' && (
                <div className="space-y-6 text-left animate-in fade-in duration-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <h1 className="text-2xl font-bold tracking-tight font-sans">Gestão de Usuários e Permissões</h1>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Controle de acesso e atribuições de responsabilidades no gabinete parlamentar.</p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedUserForEdit(null);
                        setNewUserName('');
                        setNewUserEmail('');
                        setNewUserPassword('');
                        setNewUserRoleId(rolesList[0]?.id || '');
                        setNewUserPhone('');
                        setNewUserJobTitle('');
                        setNewUserThematicAreas([]);
                        setNewUserPrefs([
                          { channel: 'INTERNAL', receiveScope: 'ALL' },
                          { channel: 'WHATSAPP', receiveScope: 'NONE' },
                          { channel: 'EMAIL', receiveScope: 'NONE' },
                        ]);
                        setIsAddUserOpen(true);
                      }}
                      className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-blue-500/10 transition-all focus:outline-none"
                    >
                      <Plus className="w-4 h-4" />
                      Novo Usuário
                    </button>
                  </div>

                  <div className="glass-panel p-5 rounded-3xl overflow-hidden border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 dark:bg-slate-900 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-200 dark:border-slate-800">
                        <tr>
                          <th className="p-3">Nome</th>
                          <th className="p-3">E-mail</th>
                          <th className="p-3">Role / Cargo</th>
                          <th className="p-3">Status</th>
                          <th className="p-3">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersList.length > 0 ? (
                          usersList.map(user => (
                            <tr key={user.id} className="border-b border-slate-100 dark:border-slate-850 hover:bg-slate-500/5">
                              <td className="p-3 font-semibold">{user.name}</td>
                              <td className="p-3 text-slate-500">{user.email}</td>
                              <td className="p-3">
                                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-400 rounded font-semibold text-[10px]">
                                  {user.roleDetails?.name || (typeof user.role === 'string' ? user.role : (user.role as any)?.name) || 'Assessor'}
                                </span>
                              </td>
                              <td className="p-3">
                                <span className={user.isActive ? "text-emerald-500 font-bold" : "text-red-500 font-bold"}>
                                  {user.isActive ? "Ativo" : "Inativo"}
                                </span>
                              </td>
                              <td className="p-3 space-x-3">
                                <button 
                                  onClick={() => handleOpenEditUser(user)}
                                  className="text-xs text-amber-500 hover:underline font-semibold"
                                >
                                  Editar
                                </button>
                                <button 
                                  onClick={() => handleRemoveUser(user.id, user.name)}
                                  className="text-xs text-red-500 hover:underline font-semibold"
                                >
                                  Remover
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="p-10 text-center text-slate-550 italic">
                              Carregando usuários do sistema...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ABA: AUDITORIA */}
              {currentTab === 'audit' && (
                <AuditLogs usersList={usersList} />
              )}

              {/* ABA: PERFIL DO USUÁRIO */}
              {currentTab === 'profile' && (
                <Profile user={currentUser} onUpdate={(u) => setCurrentUser(u)} />
              )}
            </>
          )}
          </ErrorBoundary>
        </div>
      </main>

      {/* MOBILE: Bottom Navigation */}
      <BottomNavigation
        currentTab={selectedDemandId ? '' : currentTab}
        onTabChange={(tab) => { 
          if (tab === 'alerts') {
            setIsNotificationsOpen(true);
          } else {
            setCurrentTab(tab); 
            setSelectedDemandId(null); 
            setSelectedDemandForEdit(false); 
          }
        }}
        onLogout={handleLogout}
        tabsAvailable={tabsToRender.map(t => t.id)}
        demands={demands}
        unreadCount={unreadCount}
        userName={currentUser.name}
        userRole={currentUser.role}
      />

      {/* 3. MODAL: NOVA DEMANDA (Cadastro Rápido / Upload IA) */}
      {isNewDemandOpen && (
        <div className="fixed inset-0 bg-white dark:bg-slate-900 md:bg-slate-950/70 md:backdrop-blur-sm z-50 flex items-center justify-center md:p-4">
          <div className="bg-white dark:bg-slate-900 border-0 md:border md:border-slate-200 dark:md:border-slate-800 rounded-none md:rounded-3xl w-full h-full md:h-auto md:max-h-[90vh] max-w-lg p-5 md:p-6 md:shadow-2xl relative text-left flex flex-col overflow-y-auto">
            <button
              onClick={() => setIsNewDemandOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-600 transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h2 className="text-lg font-bold">Nova Demanda</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Selecione a origem da demanda e registre os dados no sistema.
              </p>
            </div>

            {/* Origem da Demanda */}
            <div className="grid grid-cols-5 gap-2 mb-5">
              {[
                { id: 'WHATSAPP', label: 'WhatsApp', icon: <Smartphone className="w-3.5 h-3.5" /> },
                { id: 'EMAIL', label: 'E-mail', icon: <Mail className="w-3.5 h-3.5" /> },
                { id: 'PDF', label: 'PDF Upload', icon: <Upload className="w-3.5 h-3.5" /> },
                { id: 'IMAGE', label: 'Imagem', icon: <Upload className="w-3.5 h-3.5" /> },
                { id: 'MANUAL', label: 'Manual', icon: <Plus className="w-3.5 h-3.5" /> }
              ].map(source => (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => {
                    setNewDemandSource(source.id as any);
                    setUploadSuccess(false);
                  }}
                  className={`py-2 px-1 border rounded-xl flex flex-col items-center justify-center gap-1 transition-all text-[10px] font-semibold ${
                    newDemandSource === source.id
                      ? 'border-blue-500 bg-blue-500/5 text-blue-600'
                      : 'border-slate-200 dark:border-slate-850 hover:bg-slate-500/5 text-slate-500'
                  }`}
                >
                  {source.icon}
                  {source.label}
                </button>
              ))}
            </div>

            {/* Upload Area for files (WhatsApp, Email, PDF, IMAGE) */}
            {newDemandSource !== 'MANUAL' && (
              <div className="mb-5 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 text-center relative overflow-hidden">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept={newDemandSource === 'PDF' ? 'application/pdf' : 'image/*,application/pdf'}
                  onChange={handleSimulatedUpload}
                  disabled={uploadLoading}
                />
                <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center justify-center py-2.5 space-y-2">
                  {uploadLoading ? (
                    <>
                      <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                      <span className="text-xs font-semibold text-blue-500 animate-pulse">Lendo documento com IA...</span>
                    </>
                  ) : uploadSuccess ? (
                    <>
                      <Sparkles className="w-8 h-8 text-emerald-500 animate-bounce" />
                      <span className="text-xs font-semibold text-emerald-500">Documento lido e preenchido!</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-400" />
                      <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">Arrastar ou anexar Ofício</span>
                      <span className="text-[9px] text-slate-400">Leitura automatizada via OpenAI Vision (OCR)</span>
                    </>
                  )}
                </label>
              </div>
            )}

            {/* Formulário */}
            <form onSubmit={handleCreateManualDemand} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Assunto / Título</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Aquisição de Ambulância UTI"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none focus:border-blue-500 text-slate-800 dark:text-slate-100 font-medium"
                    value={newDemandTitle}
                    onChange={(e) => setNewDemandTitle(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Solicitante (Entidade)</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Santa Casa de Marília"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newDemandSolicitante}
                    onChange={(e) => setNewDemandSolicitante(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Cidade</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Marília"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newDemandCity}
                    onChange={(e) => setNewDemandCity(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Responsável / Cargo (Contato)</label>
                  <input
                    type="text"
                    placeholder="Ex: Maria Silva"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newDemandResponsible}
                    onChange={(e) => setNewDemandResponsible(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Área Temática</label>
                  <select
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                    value={newDemandArea}
                    onChange={(e) => setNewDemandArea(e.target.value)}
                  >
                    <option value="Saúde">Saúde</option>
                    <option value="Educação">Educação</option>
                    <option value="Infraestrutura">Infraestrutura</option>
                    <option value="Segurança Pública">Segurança Pública</option>
                    <option value="Assistência Social">Assistência Social</option>
                    <option value="Habitação">Habitação</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Gabinete</label>
                  <select
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                    value={newDemandCabinet}
                    onChange={(e) => setNewDemandCabinet(e.target.value as any)}
                  >
                    <option value="STATE">Dani Alonso</option>
                    <option value="FEDERAL">Cap. Augusto</option>
                    <option value="BOTH">Ambos</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Valor Solicitado (R$)</label>
                  <input
                    type="number"
                    placeholder="Ex: 150000"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newDemandValue}
                    onChange={(e) => setNewDemandValue(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Descrição da Demanda</label>
                <textarea
                  rows={2}
                  placeholder="Escreva detalhadamente o teor da demanda ou a descrição do pedido..."
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-250 dark:border-slate-800 rounded-xl p-2.5 focus:outline-none text-slate-700 dark:text-slate-200 resize-none font-sans"
                  value={newDemandSummary}
                  onChange={(e) => setNewDemandSummary(e.target.value)}
                />
              </div>

              {/* Botões de Ação */}
              <div className="pt-2 flex gap-2">
                {newDemandSource === 'MANUAL' && (
                  <button
                    type="button"
                    onClick={handleProcessManualIA}
                    disabled={isProcessingIA}
                    className="flex items-center justify-center gap-1.5 px-4 bg-purple-650 hover:bg-purple-550 disabled:bg-purple-800 text-white font-bold rounded-xl transition-all shadow-md shadow-purple-500/10 text-xs shrink-0"
                  >
                    {isProcessingIA ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Processar com IA
                  </button>
                )}
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 text-xs"
                >
                  Salvar Demanda
                </button>
                <button
                  type="button"
                  onClick={() => setIsNewDemandOpen(false)}
                  className="px-4 py-3 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 hover:bg-slate-200 dark:hover:bg-slate-900 text-slate-500 rounded-xl transition-all text-xs font-semibold"
                >
                  Fechar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: EDITAR CONTATO */}
      {isEditContactOpen && selectedContactForEdit && (
        <div className="fixed inset-0 bg-white dark:bg-slate-900 md:bg-slate-950/70 md:backdrop-blur-sm z-50 flex items-center justify-center md:p-4">
          <div className="bg-white dark:bg-slate-900 border-0 md:border md:border-slate-200 dark:md:border-slate-800 rounded-none md:rounded-3xl w-full h-full md:h-auto md:max-h-[90vh] max-w-lg p-5 md:p-6 md:shadow-2xl relative text-left flex flex-col overflow-y-auto">
            <button
              onClick={() => { setIsEditContactOpen(false); setSelectedContactForEdit(null); }}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-650 transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h2 className="text-lg font-bold">Editar Contato do Gabinete</h2>
              <p className="text-xs text-slate-500">Altere os dados da entidade ou solicitante.</p>
            </div>

            <form onSubmit={handleUpdateContact} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Nome / Entidade</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactName}
                    onChange={(e) => setEditContactName(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Tipo</label>
                  <select
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                    value={editContactType}
                    onChange={(e) => setEditContactType(e.target.value as any)}
                  >
                    <option value="PF">Pessoa Física</option>
                    <option value="PREFEITURA">Prefeitura</option>
                    <option value="HOSPITAL">Hospital</option>
                    <option value="OSC">OSC</option>
                    <option value="ORGAO_PUBLICO">Órgão Público</option>
                    <option value="ASSOCIACAO">Associação</option>
                    <option value="EMPRESA">Empresa</option>
                    <option value="OUTRO">Outro</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Responsável</label>
                  <input
                    type="text"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactResponsible}
                    onChange={(e) => setEditContactResponsible(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Telefone</label>
                  <input
                    type="text"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactPhone}
                    onChange={(e) => setEditContactPhone(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-slate-400 mb-1 font-medium">Cidade</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactCity}
                    onChange={(e) => setEditContactCity(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">UF</label>
                  <input
                    type="text"
                    required
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactUf}
                    onChange={(e) => setEditContactUf(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                  <input
                    type="email"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactEmail}
                    onChange={(e) => setEditContactEmail(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Website</label>
                  <input
                    type="text"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={editContactWebsite}
                    onChange={(e) => setEditContactWebsite(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Endereço</label>
                <input
                  type="text"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 text-slate-800 dark:text-slate-100"
                  value={editContactAddress}
                  onChange={(e) => setEditContactAddress(e.target.value)}
                />
              </div>

              <div className="pt-2 flex gap-2">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 text-xs"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => { setIsEditContactOpen(false); setSelectedContactForEdit(null); }}
                  className="px-4 py-3 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 hover:bg-slate-200 dark:hover:bg-slate-900 text-slate-500 rounded-xl transition-all text-xs font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADICIONAR USUÁRIO */}
      {isAddUserOpen && (
        <div className="fixed inset-0 bg-white dark:bg-slate-900 md:bg-slate-950/70 md:backdrop-blur-sm z-50 flex items-center justify-center md:p-4">
          <div className="bg-white dark:bg-slate-900 border-0 md:border md:border-slate-200 dark:md:border-slate-800 rounded-none md:rounded-3xl w-full h-full md:h-auto md:max-h-[90vh] max-w-md p-5 md:p-6 md:shadow-2xl relative text-left flex flex-col overflow-y-auto">
            <button
              onClick={() => setIsAddUserOpen(false)}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-650 transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h2 className="text-lg font-bold">Adicionar Novo Usuário</h2>
              <p className="text-xs text-slate-500">Crie as credenciais e defina o cargo do assessor.</p>
            </div>

            <form onSubmit={handleAddUser} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-400 mb-1 font-medium">Nome Completo</label>
                <input
                  type="text"
                  required
                  placeholder="Nome do assessor"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                <input
                  type="email"
                  required
                  placeholder="exemplo@gabinete.com.br"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Senha</label>
                <input
                  type="password"
                  required
                  placeholder="Senha de acesso"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Cargo (Role)</label>
                <select
                  required
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                  value={newUserRoleId}
                  onChange={(e) => setNewUserRoleId(e.target.value)}
                >
                  <option value="">Selecione um Cargo</option>
                  {rolesList.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Cargo / Função</label>
                  <input
                    type="text"
                    placeholder="Ex: Assessor(a) de Saúde"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newUserJobTitle}
                    onChange={(e) => setNewUserJobTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Telefone (WhatsApp)</label>
                  <input
                    type="text"
                    placeholder="(11) 99999-9999"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newUserPhone}
                    onChange={(e) => setNewUserPhone(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Áreas Temáticas de Atuação</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {['Saúde', 'Educação', 'Infraestrutura', 'Segurança Pública', 'Assistência Social', 'Habitação'].map(area => (
                    <label key={area} className="flex items-center gap-2 cursor-pointer p-1.5 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <input 
                        type="checkbox" 
                        className="accent-blue-600 w-3.5 h-3.5 rounded"
                        checked={newUserThematicAreas.includes(area)}
                        onChange={(e) => {
                          if (e.target.checked) setNewUserThematicAreas([...newUserThematicAreas, area]);
                          else setNewUserThematicAreas(newUserThematicAreas.filter(a => a !== area));
                        }}
                      />
                      <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">{area}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Escopo de Notificações</label>
                <select
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                  value={newUserPrefs.find(p => p.channel === 'INTERNAL')?.receiveScope || 'ALL'}
                  onChange={(e) => {
                    const newScope = e.target.value;
                    setNewUserPrefs([
                      { channel: 'INTERNAL', receiveScope: newScope },
                      { channel: 'WHATSAPP', receiveScope: newScope },
                      { channel: 'EMAIL', receiveScope: newScope },
                    ]);
                  }}
                >
                  <option value="ALL">ALL - Receber de Todas as Áreas</option>
                  <option value="AREA_ONLY">AREA_ONLY - Apenas das Minhas Áreas</option>
                  <option value="NONE">NONE - Não Receber Notificações</option>
                </select>
              </div>

              <div className="pt-2 flex gap-2">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 text-xs"
                >
                  Criar Usuário
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddUserOpen(false)}
                  className="px-4 py-3 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 hover:bg-slate-200 dark:hover:bg-slate-900 text-slate-500 rounded-xl transition-all text-xs font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: EDITAR USUÁRIO */}
      {isEditUserOpen && selectedUserForEdit && (
        <div className="fixed inset-0 bg-white dark:bg-slate-900 md:bg-slate-950/70 md:backdrop-blur-sm z-50 flex items-center justify-center md:p-4">
          <div className="bg-white dark:bg-slate-900 border-0 md:border md:border-slate-200 dark:md:border-slate-800 rounded-none md:rounded-3xl w-full h-full md:h-auto md:max-h-[90vh] max-w-md p-5 md:p-6 md:shadow-2xl relative text-left flex flex-col overflow-y-auto">
            <button
              onClick={() => { setIsEditUserOpen(false); setSelectedUserForEdit(null); }}
              className="absolute right-4 top-4 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-400 hover:text-slate-650 transition-all"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="mb-4">
              <h2 className="text-lg font-bold">Editar Usuário</h2>
              <p className="text-xs text-slate-500">Atualize os dados e atribuição do cargo do assessor.</p>
            </div>

            <form onSubmit={handleEditUser} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-400 mb-1 font-medium">Nome Completo</label>
                <input
                  type="text"
                  required
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">E-mail</label>
                <input
                  type="email"
                  required
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Cargo (Role)</label>
                <select
                  required
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                  value={newUserRoleId}
                  onChange={(e) => setNewUserRoleId(e.target.value)}
                >
                  <option value="">Selecione um Cargo</option>
                  {rolesList.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Cargo / Função</label>
                  <input
                    type="text"
                    placeholder="Ex: Assessor(a) de Saúde"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newUserJobTitle}
                    onChange={(e) => setNewUserJobTitle(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-slate-400 mb-1 font-medium">Telefone (WhatsApp)</label>
                  <input
                    type="text"
                    placeholder="(11) 99999-9999"
                    className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100"
                    value={newUserPhone}
                    onChange={(e) => setNewUserPhone(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Áreas Temáticas de Atuação</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {['Saúde', 'Educação', 'Infraestrutura', 'Segurança Pública', 'Assistência Social', 'Habitação'].map(area => (
                    <label key={area} className="flex items-center gap-2 cursor-pointer p-1.5 border border-slate-200 dark:border-slate-800 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <input 
                        type="checkbox" 
                        className="accent-blue-600 w-3.5 h-3.5 rounded"
                        checked={newUserThematicAreas.includes(area)}
                        onChange={(e) => {
                          if (e.target.checked) setNewUserThematicAreas([...newUserThematicAreas, area]);
                          else setNewUserThematicAreas(newUserThematicAreas.filter(a => a !== area));
                        }}
                      />
                      <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300">{area}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-slate-400 mb-1 font-medium">Escopo de Notificações</label>
                <select
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-255 dark:border-slate-800 rounded-xl p-2 focus:outline-none text-slate-800 dark:text-slate-100 font-semibold"
                  value={newUserPrefs.find(p => p.channel === 'INTERNAL')?.receiveScope || 'ALL'}
                  onChange={(e) => {
                    const newScope = e.target.value;
                    setNewUserPrefs([
                      { channel: 'INTERNAL', receiveScope: newScope },
                      { channel: 'WHATSAPP', receiveScope: newScope },
                      { channel: 'EMAIL', receiveScope: newScope },
                    ]);
                  }}
                >
                  <option value="ALL">ALL - Receber de Todas as Áreas</option>
                  <option value="AREA_ONLY">AREA_ONLY - Apenas das Minhas Áreas</option>
                  <option value="NONE">NONE - Não Receber Notificações</option>
                </select>
              </div>

              <div className="pt-2 flex gap-2">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-md shadow-blue-500/10 text-xs"
                >
                  Salvar Alterações
                </button>
                <button
                  type="button"
                  onClick={() => { setIsEditUserOpen(false); setSelectedUserForEdit(null); }}
                  className="px-4 py-3 bg-slate-100 dark:bg-slate-950 border border-slate-200 dark:border-slate-850 hover:bg-slate-200 dark:hover:bg-slate-900 text-slate-500 rounded-xl transition-all text-xs font-semibold"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Renderizar modal de detalhes do contato */}
      <ContactDetailModal
        isOpen={isDetailContactOpen}
        onClose={() => {
          setIsDetailContactOpen(false);
          setSelectedContactForDetail(null);
        }}
        contact={selectedContactForDetail}
        onViewDemands={() => {
          if (selectedContactForDetail) {
            setGlobalSearchQuery(selectedContactForDetail.nameEntity);
            setCurrentTab('demands');
            setIsDetailContactOpen(false);
            setSelectedContactForDetail(null);
          }
        }}
      />

    </div>
    </ErrorBoundary>
  );
};

export default App;
